import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BiCar, BiFilterAlt, BiGasPump, BiMapPin, BiRightArrowAlt, BiSearch, BiStar, BiUser, BiX } from "react-icons/bi";
import { GiGearStick } from "react-icons/gi";
import Handler from "../../../Helpers/Handler";
import BackButton from "../../../Components/BackButton";
import { currencyFormatter, textCapitalize } from "../../../Helpers/Utils";
import BookingRangePicker from "../../../Components/BookingRangePicker";
import { DEFAULT_RULES, createDateRules, formatRangeDate, rulesFromSettings, toDate } from "../../../Helpers/dateRange";

// Reached from Bookings > Book Now. Editing the trip window, hub, search or
// any filter reloads the list in place (debounced) - no separate "search"
// button. Everything that shapes the result (window, hub, search, filters,
// sort) lives in the URL, so Back from Register returns to the same list and
// a search can be shared. Clicking a car carries the chosen window straight
// into Registration so nothing has to be re-entered.

const EMPTY_FILTERS = { brand: [], segment: [], fuelType: [], transmissionType: [], seats: [], minPrice: "", maxPrice: "", sort: "name" };
const LIST_KEYS = ["brand", "segment", "fuelType", "transmissionType", "seats"];
const SORTS = [
    { value: "name", label: "Name (A-Z)" },
    { value: "price-asc", label: "Price: low to high" },
    { value: "price-desc", label: "Price: high to low" },
    { value: "newest", label: "Newest model" },
    { value: "rating", label: "Top rated" },
];
const BRAND_VISIBLE = 6;

// The earliest legal pickup and a 24-hour trip under the given rules.
const defaultWindow = (dates) => {
    const start = dates.earliestStart();
    const { start: s, end } = dates.clampRange({ start, end: new Date(start.getTime() + 24 * 36e5) });
    return { start: s, end };
};

const hubLabel = (hub) => [hub.area, hub.city].filter(Boolean).join(", ") || hub.hubId;

const lowestRate = (car) => {
    const rates = (car?.priceTypes || []).map((p) => Number(p?.perHourPrice)).filter((n) => Number.isFinite(n));
    return rates.length ? Math.min(...rates) : null;
};

const tripLabel = (hours) => {
    const days = Math.floor(hours / 24);
    const rest = hours % 24;
    if (!days) return `${hours} hr${hours === 1 ? "" : "s"}`;
    return rest ? `${days}d ${rest}h` : `${days} day${days === 1 ? "" : "s"}`;
};

const asArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);

const readFilters = (params) => {
    const filters = { ...EMPTY_FILTERS };
    LIST_KEYS.forEach((key) => { filters[key] = (params.get(key) || "").split(",").filter(Boolean); });
    filters.minPrice = params.get("min") || "";
    filters.maxPrice = params.get("max") || "";
    filters.sort = SORTS.some((s) => s.value === params.get("sort")) ? params.get("sort") : "name";
    return filters;
};

const countActive = (filters) =>
    LIST_KEYS.reduce((n, key) => n + filters[key].length, 0) + (filters.minPrice !== "" ? 1 : 0) + (filters.maxPrice !== "" ? 1 : 0);

/* ---------------- filter sidebar pieces ---------------- */

const FacetGroup = ({ title, options, selected, onToggle, format = (v) => v, limit = Infinity }) => {
    const [expanded, setExpanded] = useState(false);
    // A selected value the current window no longer offers stays listed (count 0) so it can be unticked.
    const merged = [...options, ...selected.filter((v) => !options.some((o) => String(o.value) === String(v))).map((value) => ({ value, count: 0 }))];
    if (!merged.length) return null;
    const shown = expanded ? merged : merged.filter((o, i) => i < limit || selected.includes(String(o.value)));
    return (
        <fieldset className="av-facet">
            <legend>{title}</legend>
            {shown.map((option) => {
                const value = String(option.value);
                const checked = selected.includes(value);
                const id = `av-${title}-${value}`.replace(/\s+/g, "-");
                return (
                    <label key={value} htmlFor={id} className={`av-check${checked ? " is-checked" : ""}${option.count === 0 && !checked ? " is-empty" : ""}`}>
                        <input id={id} type="checkbox" checked={checked} onChange={() => onToggle(value)} />
                        <span className="av-check-box" aria-hidden="true" />
                        <span className="av-check-label">{format(option.value)}</span>
                        <span className="av-check-count">{option.count}</span>
                    </label>
                );
            })}
            {merged.length > shown.length && (
                <button type="button" className="av-more" onClick={() => setExpanded(true)}>Show {merged.length - shown.length} more</button>
            )}
            {expanded && merged.length > limit && (
                <button type="button" className="av-more" onClick={() => setExpanded(false)}>Show less</button>
            )}
        </fieldset>
    );
};

const FilterPanel = ({ facets, filters, setFilters, activeCount, onClear, onClose, total, loading }) => {
    const toggle = (key) => (value) =>
        setFilters((prev) => ({ ...prev, [key]: prev[key].includes(value) ? prev[key].filter((v) => v !== value) : [...prev[key], value] }));
    const price = facets?.price || {};
    return (
        <>
            <div className="av-aside-head">
                <div className="av-aside-title"><BiFilterAlt /> Filters{activeCount > 0 && <span className="av-badge">{activeCount}</span>}</div>
                <div className="av-aside-actions">
                    {activeCount > 0 && <button type="button" className="av-link" onClick={onClear}>Clear all</button>}
                    <button type="button" className="av-icon-btn av-only-sheet" onClick={onClose} aria-label="Close filters"><BiX /></button>
                </div>
            </div>

            <div className="av-aside-body">
                <FacetGroup title="Brand" options={facets?.brands || []} selected={filters.brand} onToggle={toggle("brand")} limit={BRAND_VISIBLE} />
                <FacetGroup title="Body type" options={facets?.segments || []} selected={filters.segment} onToggle={toggle("segment")} format={textCapitalize} />
                <FacetGroup title="Fuel" options={facets?.fuelTypes || []} selected={filters.fuelType} onToggle={toggle("fuelType")} format={textCapitalize} />
                <FacetGroup title="Transmission" options={facets?.transmissions || []} selected={filters.transmissionType} onToggle={toggle("transmissionType")} />
                <FacetGroup title="Seats" options={facets?.seats || []} selected={filters.seats} onToggle={toggle("seats")} format={(v) => `${v} seats`} />

                {/* <fieldset className="av-facet">
                    <legend>Price per hour</legend>
                    <div className="av-price">
                        <div className="av-price-field">
                            <span>₹</span>
                            <input type="number" min="0" inputMode="numeric" aria-label="Minimum price per hour" placeholder={price.min ?? "Min"} value={filters.minPrice} onChange={(e) => setFilters((p) => ({ ...p, minPrice: e.target.value }))} />
                        </div>
                        <span className="av-price-to">to</span>
                        <div className="av-price-field">
                            <span>₹</span>
                            <input type="number" min="0" inputMode="numeric" aria-label="Maximum price per hour" placeholder={price.max ?? "Max"} value={filters.maxPrice} onChange={(e) => setFilters((p) => ({ ...p, maxPrice: e.target.value }))} />
                        </div>
                    </div>
                    {price.min !== null && price.min !== undefined && (
                        <div className="av-hint">Free cars range {currencyFormatter(price.min)} – {currencyFormatter(price.max)} /hr (lowest package rate)</div>
                    )}
                </fieldset> */}
            </div>

            <div className="av-aside-foot av-only-sheet">
                <button type="button" className="av-btn av-btn--primary" onClick={onClose}>
                    {loading ? "Updating…" : `Show ${total} car${total === 1 ? "" : "s"}`}
                </button>
            </div>
        </>
    );
};

/* ---------------- car card ---------------- */

const CarCard = ({ car, hours, hubName, onPick }) => {
    const rate = lowestRate(car);
    const fuel = asArray(car.fuelType).map(textCapitalize).join(" / ");
    return (
        <button type="button" className="av-card" onClick={() => onPick(car)} aria-label={`Select ${car.brand} ${car.modelName}, ${car.registrationNo}`}>
            <span className="av-card-media">
                {car.carImages?.[0] ? <img src={car.carImages[0]} alt="" loading="lazy" /> : <span className="av-card-noimg"><BiCar /></span>}
                {car.segment && <span className="av-card-tag">{textCapitalize(car.segment)}</span>}
                {car.star ? <span className="av-card-rating"><BiStar /> {Number(car.star).toFixed(1)}</span> : null}
            </span>
            <span className="av-card-body">
                <span className="av-card-title">{car.brand} {car.modelName}</span>
                <span className="av-card-sub">{[car.variantName, car.year].filter(Boolean).join(" · ")}</span>
                <span className="av-specs">
                    {fuel && <span><BiGasPump /> {fuel}</span>}
                    {car.transmissionType && <span><GiGearStick /> {car.transmissionType}</span>}
                    {car.seats ? <span><BiUser /> {car.seats} seats</span> : null}
                </span>
                <span className="av-card-meta">
                    <span className="av-reg">{car.registrationNo}</span>
                    {hubName && <span className="av-hub"><BiMapPin /> {hubName}</span>}
                </span>
            </span>
            <span className="av-card-foot">
                <span className="av-price-block">
                    {rate !== null ? (
                        <>
                            <span className="av-price-main">from {currencyFormatter(rate)}<small> /hr</small></span>
                            <span className="av-price-est">≈ {currencyFormatter(Math.round(rate * hours))} for {tripLabel(hours)}</span>
                        </>
                    ) : <span className="av-price-est">Price on request</span>}
                </span>
                <span className="av-select">Select <BiRightArrowAlt /></span>
            </span>
        </button>
    );
};

const SkeletonCard = () => (
    <div className="av-card av-card--skeleton" aria-hidden="true">
        <span className="av-card-media" />
        <span className="av-card-body"><span className="av-sk av-sk--title" /><span className="av-sk av-sk--sub" /><span className="av-sk av-sk--row" /></span>
        <span className="av-card-foot"><span className="av-sk av-sk--price" /></span>
    </div>
);

/* ---------------- page ---------------- */

const BookingAvailability = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    // Read once: the URL is written from state afterwards, never re-read.
    const initial = useRef(null);
    if (initial.current === null) {
        const s = toDate(searchParams.get("s"));
        const e = toDate(searchParams.get("e"));
        initial.current = { start: s, end: e, hub: searchParams.get("hub") || "", search: searchParams.get("q") || "", filters: readFilters(searchParams) };
    }

    // Date rules start at the server's defaults and are replaced by the real
    // admin-channel ones (lead time + trip length/horizon, exactly what
    // BookingUtils enforces) once /admin/booking/lead-time loads. Searching
    // waits for that, so the list isn't fetched twice for two different windows.
    const [dates, setDates] = useState(() => createDateRules(DEFAULT_RULES));
    const [rulesReady, setRulesReady] = useState(false);
    const [range, setRange] = useState(() => (initial.current.start && initial.current.end ? dates.clampRange({ start: initial.current.start, end: initial.current.end }) : defaultWindow(dates)));
    const rangeRef = useRef(range);
    rangeRef.current = range;
    const touchedRef = useRef(!!(initial.current.start && initial.current.end));
    const { start: startDate, end: endDate } = range;
    const onRangeChange = (next) => { touchedRef.current = true; setRange({ start: next.start, end: next.end }); };

    const [hub, setHub] = useState(initial.current.hub);
    const [search, setSearch] = useState(initial.current.search);
    const [filters, setFilters] = useState(initial.current.filters);
    const [hubs, setHubs] = useState([]);
    const [result, setResult] = useState(null); // { list, total, availableTotal, truncated, facets }
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const debounceRef = useRef(null);
    const requestRef = useRef(0);

    const hubNames = useMemo(() => Object.fromEntries(hubs.map((h) => [h.hubId, hubLabel(h)])), [hubs]);
    const activeCount = countActive(filters);
    const hours = dates.tripHours(range);

    useEffect(() => {
        (async () => {
            const response = await Handler({ method: "get", url: "/admin/hub/active" });
            if (response?.success) setHubs(response?.data?.data || []);
        })();
    }, []);

    useEffect(() => {
        (async () => {
            const response = await Handler({ method: "get", url: "/admin/booking/lead-time" });
            const leadHours = Number(response?.data?.data?.hours);
            if (response?.success && Number.isFinite(leadHours)) {
                const real = createDateRules(rulesFromSettings({ leadTime: leadHours, duration: response?.data?.data?.duration }, "admin"));
                setDates(real);
                // Untouched: restart from the real earliest pickup. Touched (or from the URL):
                // keep the admin's choice, clamping only if the real rules forbid it.
                setRange(touchedRef.current ? real.clampRange(rangeRef.current) : defaultWindow(real));
            }
            // If the call failed the defaults stand - searching must still start.
            setRulesReady(true);
        })();
    }, []);

    const fetchCars = useCallback(async () => {
        if (!rulesReady) return;
        const request = ++requestRef.current;
        setError(null);
        setLoading(true);
        const response = await Handler({
            method: "post",
            url: "/admin/booking/available-cars",
            data: {
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
                ...(hub ? { hub } : {}),
                ...(search.trim() ? { search: search.trim() } : {}),
                ...LIST_KEYS.reduce((acc, key) => (filters[key].length ? { ...acc, [key]: key === "seats" ? filters[key].map(Number) : filters[key] } : acc), {}),
                ...(filters.minPrice !== "" ? { minPrice: Number(filters.minPrice) } : {}),
                ...(filters.maxPrice !== "" ? { maxPrice: Number(filters.maxPrice) } : {}),
                sort: filters.sort,
            },
        });
        if (request !== requestRef.current) return; // a newer search superseded this one
        if (response?.success) {
            setResult(response?.data?.data || null);
        } else {
            setResult((prev) => (prev ? { ...prev, list: [], total: 0 } : prev));
            setError(response?.data?.message || "Couldn't load available cars");
        }
        setLoading(false);
    }, [rulesReady, startDate, endDate, hub, search, filters]);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(fetchCars, 350);
        return () => clearTimeout(debounceRef.current);
    }, [fetchCars]);

    // Mirror the state into the URL (replace, so it doesn't fill the history).
    useEffect(() => {
        if (!rulesReady) return;
        const params = new URLSearchParams();
        params.set("s", String(startDate.getTime()));
        params.set("e", String(endDate.getTime()));
        if (hub) params.set("hub", hub);
        if (search.trim()) params.set("q", search.trim());
        LIST_KEYS.forEach((key) => { if (filters[key].length) params.set(key, filters[key].join(",")); });
        if (filters.minPrice !== "") params.set("min", filters.minPrice);
        if (filters.maxPrice !== "") params.set("max", filters.maxPrice);
        if (filters.sort !== "name") params.set("sort", filters.sort);
        setSearchParams(params, { replace: true });
    }, [rulesReady, startDate, endDate, hub, search, filters, setSearchParams]);

    // Bottom-sheet filters (narrow screens): Escape closes, page doesn't scroll behind.
    useEffect(() => {
        if (!filtersOpen) return undefined;
        const onKey = (event) => { if (event.key === "Escape") setFiltersOpen(false); };
        document.addEventListener("keydown", onKey);
        const previous = document.body.style.overflow;
        if (window.innerWidth < 992) document.body.style.overflow = "hidden";
        return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = previous; };
    }, [filtersOpen]);

    const pickCar = (car) => {
        const params = new URLSearchParams({
            carId: car.carId,
            // Epoch ms, the same convention the marketing site's handoff uses;
            // Register reads these (and still reads ISO strings from old links).
            startDate: String(startDate.getTime()),
            endDate: String(endDate.getTime()),
        });
        navigate(`/booking/register?${params.toString()}`);
    };

    const clearFilters = () => setFilters((prev) => ({ ...EMPTY_FILTERS, sort: prev.sort }));
    const clearEverything = () => { clearFilters(); setSearch(""); setHub(""); };

    // Active filters as removable chips.
    const chips = [
        ...LIST_KEYS.flatMap((key) => filters[key].map((value) => ({
            key: `${key}:${value}`,
            label: key === "seats" ? `${value} seats` : key === "segment" || key === "fuelType" ? textCapitalize(value) : value,
            remove: () => setFilters((prev) => ({ ...prev, [key]: prev[key].filter((v) => v !== value) })),
        }))),
        ...(filters.minPrice !== "" || filters.maxPrice !== "" ? [{
            key: "price",
            label: `${filters.minPrice !== "" ? currencyFormatter(filters.minPrice) : "₹0"} – ${filters.maxPrice !== "" ? currencyFormatter(filters.maxPrice) : "any"} /hr`,
            remove: () => setFilters((prev) => ({ ...prev, minPrice: "", maxPrice: "" })),
        }] : []),
    ];

    const list = result?.list || [];
    const total = result?.total ?? 0;
    const availableTotal = result?.availableTotal ?? 0;
    const firstLoad = !result && loading;

    return (
        <div className="container-fluid py-4 av">
            <BackButton className="mb-3" />

            <div className="av-header">
                <div>
                    <h5 className="av-title">Availability</h5>
                    <div className="av-subtitle">
                        Cars free from <strong>{formatRangeDate(startDate)}</strong> to <strong>{formatRangeDate(endDate)}</strong> <span className="av-tz">IST</span> &middot; {tripLabel(hours)}
                    </div>
                </div>
            </div>


            <div className="av-layout">
                {filtersOpen && <div className="av-backdrop" onMouseDown={() => setFiltersOpen(false)} aria-hidden="true" />}
                <aside className={`av-aside${filtersOpen ? " is-open" : ""}`} aria-label="Filters">
                    <FilterPanel
                        facets={result?.facets}
                        filters={filters}
                        setFilters={setFilters}
                        activeCount={activeCount}
                        onClear={clearFilters}
                        onClose={() => setFiltersOpen(false)}
                        total={total}
                        loading={loading}
                    />
                </aside>
                <div>
                    <div className="av-search">
                        <div className="av-search-range">
                            <label className="av-label" htmlFor="availability-range">Pickup &amp; drop-off</label>
                            <BookingRangePicker id="availability-range" dates={dates} value={range} onChange={onRangeChange} />
                        </div>
                        <div className="av-search-hub">
                            <label className="av-label" htmlFor="availability-hub">Location</label>
                            <div className="av-input">
                                <BiMapPin aria-hidden="true" />
                                <select id="availability-hub" value={hub} onChange={(e) => setHub(e.target.value)}>
                                    <option value="">Any hub</option>
                                    {hubs.map((h) => <option key={h.hubId} value={h.hubId}>{hubLabel(h)}</option>)}
                                </select>
                            </div>
                        </div>
                        {/* <div className="av-search-reg">
                    <label className="av-label" htmlFor="availability-reg">Registration no.</label>
                    <div className="av-input">
                        <BiSearch aria-hidden="true" />
                        <input id="availability-reg" type="text" maxLength={100} placeholder="e.g. TN 09 AB 1234" value={search} onChange={(e) => setSearch(e.target.value)} autoComplete="off" />
                        {search && <button type="button" className="av-input-clear" onClick={() => setSearch("")} aria-label="Clear search"><BiX /></button>}
                    </div>
                </div> */}
                    </div>
                    <section className="av-results" aria-live="polite" aria-busy={loading}>
                        <div className="av-toolbar">
                            <div className="av-count">
                                {firstLoad ? "Checking availability…" : (
                                    <>
                                        <strong>{total}</strong> {total === 1 ? "car" : "cars"} free
                                        {activeCount > 0 && availableTotal > 0 && <span className="av-count-of"> of {availableTotal}</span>}
                                        {result?.truncated && <span className="av-count-of"> · showing first {list.length}, narrow with filters</span>}
                                    </>
                                )}
                            </div>
                            <div className="av-toolbar-right">
                                <button type="button" className="av-btn av-btn--ghost av-only-sheet" onClick={() => setFiltersOpen(true)}>
                                    <BiFilterAlt /> Filters{activeCount > 0 && <span className="av-badge">{activeCount}</span>}
                                </button>
                                <label className="av-sort">
                                    <span>Sort</span>
                                    <select value={filters.sort} onChange={(e) => setFilters((prev) => ({ ...prev, sort: e.target.value }))}>
                                        {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                    </select>
                                </label>
                            </div>
                        </div>

                        {chips.length > 0 && (
                            <div className="av-chips">
                                {chips.map((chip) => (
                                    <button type="button" key={chip.key} className="av-chip" onClick={chip.remove} aria-label={`Remove filter ${chip.label}`}>
                                        {chip.label} <BiX />
                                    </button>
                                ))}
                                <button type="button" className="av-link" onClick={clearFilters}>Clear all</button>
                            </div>
                        )}

                        {error && <div className="av-alert" role="alert">{error}</div>}

                        {firstLoad ? (
                            <div className="av-grid">{Array.from({ length: 8 }, (_, i) => <SkeletonCard key={i} />)}</div>
                        ) : list.length === 0 && !error ? (
                            <div className="av-empty">
                                <BiCar className="av-empty-icon" />
                                {availableTotal === 0 ? (
                                    <>
                                        <h6>No cars are free for this window{hub ? " at this hub" : ""}</h6>
                                        <p>Try different pickup and drop-off times{hub ? ", another location" : ""}{search ? " or clear the registration search" : ""}.</p>
                                        {(hub || search) && <button type="button" className="av-btn av-btn--primary" onClick={clearEverything}>Clear location &amp; search</button>}
                                    </>
                                ) : (
                                    <>
                                        <h6>No cars match these filters</h6>
                                        <p>{availableTotal} {availableTotal === 1 ? "car is" : "cars are"} free for this window, but none match your filters.</p>
                                        <button type="button" className="av-btn av-btn--primary" onClick={clearFilters}>Clear filters</button>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className={`av-grid${loading ? " is-busy" : ""}`}>
                                {list.map((car) => <CarCard key={car.carId} car={car} hours={hours} hubName={hubNames[car.hub]} onPick={pickCar} />)}
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
};

export default BookingAvailability;
